"use client";
import SectionWrapper from "@cc/section-wrapper";
import {
  alpha,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { H6_3 } from "@theme/components/typography.fontvariant";
import { CloseRounded, SearchRounded } from "@mui/icons-material";
import { AllNewsFilterOptions } from "@/cms-helpers/api-helper.client";
import { create } from "zustand";
import { useAppSearchParams } from "@/hooks/app-config";

interface NewsActionSectionProps {
  title?: string;
  description?: string;
  placeholder?: string;
}

const NewsActionSection = (props: NewsActionSectionProps) => {
  const { title, description, placeholder } = props;
  const type = useAppSearchParams("type");
  const { filters, setSearchFilters } = useNewsSearchAndFilter();

  useEffect(() => {
    setSearchFilters({
      topic: type || "",
    });
  }, [setSearchFilters, type]);

  return (
    <SectionWrapper
      disablePassColorToChildren
      py={{
        xs: 2.75,
        md: 5,
        xxl: 6,
      }}
      pb={{
        xs: 0,
        md: 0,
        xxl: 0,
      }}
      SectionProps={{
        bgcolor: "common.white",
      }}
    >
      <Typography
        variant={"h2"}
        color={"primary"}
        fontSize={{ xs: 48, md: 48, xxl: 48 }}
      >
        {title || "ALL NEWS"}
      </Typography>
      <Typography fontSize={18} color={"grey.600"}>
        {description ||
          "Stay informed by exploring all the latest news stories in the Bajaj world."}
      </Typography>
      <TextField
        variant={"standard"}
        fullWidth
        placeholder={
          placeholder || "Search for a topic, company, publications and more"
        }
        onChange={(e) => {
          setSearchFilters({
            keyword: e.target.value,
          });
        }}
        InputProps={{
          sx: {
            color: (theme) => theme.palette.grey[600],
            "& input::placeholder": {
              fontSize: H6_3,
              color: (theme) => alpha(theme.palette.grey[600], 0.6),
            },
          },

          startAdornment: (
            <InputAdornment
              position={"start"}
              sx={{
                mr: {
                  xs: 1,
                  md: 2,
                },
              }}
            >
              <SearchRounded
                sx={{
                  color: "grey.600",
                }}
              />
            </InputAdornment>
          ),
          ...(filters.keyword &&
            filters.keyword.length > 0 && {
              endAdornment: (
                <InputAdornment
                  position={"end"}
                  onClick={() => {
                    setSearchFilters({
                      keyword: "",
                    });
                  }}
                >
                  <IconButton>
                    <CloseRounded
                      sx={{
                        color: "grey.600",
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }),
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: {
            xs: 56,
            md: 72,
            xl: 80,
          },
          color: "inherit",
          bgcolor: "transparent",
          fontSize: H6_3,
          mt: { xs: 3, md: 4 },
        }}
      />
    </SectionWrapper>
  );
};

export default NewsActionSection;

export interface UseNewsSearchAndFilter {
  filters: AllNewsFilterOptions;
  setSearchFilters: (filters: Partial<AllNewsFilterOptions>) => void;
}

export const useNewsSearchAndFilter = create<UseNewsSearchAndFilter>((set) => ({
  filters: {
    sort: "desc",
    keyword: "",
    organization: "",
    year: "",
    topic: "",
  },
  setSearchFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
    })),
}));
